import React, { useEffect, useState } from "react";
import IconButton from "../../../../components/IconButton/IconButton";
import "./BranchWorkingDaysCard.scss";
import axiosInstance from "../../../../axiosInstance";
import LoadingScreen from "../../../../components/LoadingScreen/LoadingScreen";
import { toast } from "react-toastify";
import WarningPopup from "../../../../components/WarningPopup/WarningPopup";
import ColoredCheckbox from "../../../../components/ColoredCheckbox/ColoredCheckbox";

const BranchWorkingDaysCard = ({ onEditWorkingDay, refreshTrigger }) => {
  const [workingDaysData, setWorkingDaysData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState({});
  const [warningPopup, setWarningPopup] = useState({
    isOpen: false,
    workingDayId: null,
  });

  const fetchWorkingDays = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/workingdays`
      );
      setWorkingDaysData(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching working days:", err);
      setError("Failed to load working days data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkingDays();
  }, []);

  // Add effect to refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchWorkingDays();
    }
  }, [refreshTrigger]);

  const handleEditClick = (workingDay) => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const editData = {
      workingDayId: workingDay.workingDayId,
      workingTypeName: workingDay.workingTypeName,
      selectedBranches:
        workingDay.branches?.map((branch) => branch.branchId) || [],
      selectedDepartments:
        workingDay.branches?.reduce((acc, branch) => {
          if (branch && branch.branchId) {
            acc[branch.branchId] = branch.departments
              ? branch.departments.map((dept) => dept.departmentId)
              : [];
          }
          return acc;
        }, {}) || {},
      selectedDays: [
        ...(workingDay.monday ? ["Monday"] : []),
        ...(workingDay.tuesday ? ["Tuesday"] : []),
        ...(workingDay.wednesday ? ["Wednesday"] : []),
        ...(workingDay.thursday ? ["Thursday"] : []),
        ...(workingDay.friday ? ["Friday"] : []),
        ...(workingDay.saturday ? ["Saturday"] : []),
        ...(workingDay.sunday ? ["Sunday"] : []),
      ],
      selectedSaturdays: [
        ...(workingDay.leaveOnFirstSaturday ? [1] : []),
        ...(workingDay.leaveOnSecondSaturday ? [2] : []),
        ...(workingDay.leaveOnThirdSaturday ? [3] : []),
        ...(workingDay.leaveOnFourthSaturday ? [4] : []),
        ...(workingDay.leaveOnFifthSaturday ? [5] : []),
      ],
      selectedSundays: [
        ...(workingDay.leaveOnFirstSunday ? [1] : []),
        ...(workingDay.leaveOnSecondSunday ? [2] : []),
        ...(workingDay.leaveOnThirdSunday ? [3] : []),
        ...(workingDay.leaveOnFourthSunday ? [4] : []),
        ...(workingDay.leaveOnFifthSunday ? [5] : []),
      ],
      isEditing: true,
    };

    onEditWorkingDay(editData);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/workingdays/${warningPopup.workingDayId}`
      );
      toast.success("Working day configuration deleted successfully!");
      await fetchWorkingDays();
    } catch (err) {
      console.error(err.response ? err.response.data : err);

      if (err.response?.status === 409) {
        const errorMessage =
          err.response.data?.message ||
          "Conflict: Cannot delete this working day configuration.";
        toast.error(errorMessage);
      } else {
        toast.error("Failed to delete the working day configuration.");
      }
    } finally {
      setWarningPopup({ isOpen: false, workingDayId: null });
    }
  };

  const handleCancelDelete = () => {
    setWarningPopup({ isOpen: false, workingDayId: null });
  };

  const handleInputChange = (e, cardIndex, day) => {
    const updatedData = [...workingDaysData];
    updatedData[cardIndex][day] = e.target.checked;
    setWorkingDaysData(updatedData);
  };

  const toggleEditMode = (workingDayId) => {
    setEditMode((prev) => ({ ...prev, [workingDayId]: !prev[workingDayId] }));
  };

  const saveWorkingDays = async (workingDayId, cardIndex) => {
    const updatedData = workingDaysData[cardIndex];
    const requestBody = {
      branchIds: updatedData.branches.map((branch) => branch.branchId),
      departmentIds: updatedData.branches.flatMap((branch) =>
        branch.departments.map((department) => department.departmentId)
      ),
      workingDaysId: updatedData.workingDayId,
      workingTypeName: updatedData.workingTypeName,
      monday: updatedData.monday,
      tuesday: updatedData.tuesday,
      wednesday: updatedData.wednesday,
      thursday: updatedData.thursday,
      friday: updatedData.friday,
      saturday: updatedData.saturday,
      sunday: updatedData.sunday,
      leaveOnFirstSaturday: updatedData.leaveOnFirstSaturday,
      leaveOnFirstSunday: updatedData.leaveOnFirstSunday,
      leaveOnSecondSaturday: updatedData.leaveOnSecondSaturday,
      leaveOnSecondSunday: updatedData.leaveOnSecondSunday,
      leaveOnThirdSaturday: updatedData.leaveOnThirdSaturday,
      leaveOnThirdSunday: updatedData.leaveOnThirdSunday,
      leaveOnFourthSaturday: updatedData.leaveOnFourthSaturday,
      leaveOnFourthSunday: updatedData.leaveOnFourthSunday,
      leaveOnFifthSaturday: updatedData.leaveOnFifthSaturday,
      leaveOnFifthSunday: updatedData.leaveOnFifthSunday,
      companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
    };

    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/workingdays`,
        requestBody
      );
      toast.success("Working days updated successfully!");
      await fetchWorkingDays();
      toggleEditMode(workingDayId);
    } catch (err) {
      console.error(err.response ? err.response.data : err);
      toast.error("Failed to update working days.");
    }
  };

  // Group branches by working day configuration
  const groupBranchesByWorkingDay = () => {
    return workingDaysData.map((workingDay) => {
      const branchGroups = {};

      workingDay.branches?.forEach((branch) => {
        const departmentIds = (branch.departments || [])
          .map((d) => d.departmentId)
          .sort()
          .join(",");

        if (!branchGroups[departmentIds]) {
          branchGroups[departmentIds] = {
            departments: branch.departments || [],
            branches: [],
          };
        }
        branchGroups[departmentIds].branches.push(branch);
      });

      return {
        ...workingDay,
        branchGroups: Object.values(branchGroups),
      };
    });
  };

  if (loading) return <LoadingScreen />;
  if (error) return <div className="error-message">{error}</div>;

  const renderWeekDots = (day, dayLabel, editMode, onChange) => (
    <div className="day-item">
      {editMode ? (
        <ColoredCheckbox
          label={dayLabel}
          color="green"
          checked={day}
          name={dayLabel}
          onChange={onChange}
        />
      ) : (
        <div className="day-display">
          <span className={`day-dot ${day ? "active" : "inactive"}`}></span>
          <span className="day-label">{dayLabel}</span>
        </div>
      )}
    </div>
  );

  const renderSpecialDayDot = (day, editMode, onChange) => (
    <div className="special-day-cell">
      {editMode ? (
        <ColoredCheckbox
          label=""
          color="green"
          checked={day}
          name="specialDay"
          onChange={onChange}
        />
      ) : (
        <span
          className={`special-day-dot ${day ? "active" : "inactive"}`}
        ></span>
      )}
    </div>
  );

  const groupedData = groupBranchesByWorkingDay();

  const hasAnyBranches = groupedData.some(
    (workingDay) => workingDay.branches && workingDay.branches.length > 0
  );

  if (!hasAnyBranches) {
    return (
      <div className="working-days-container">
        <div className="empty-state">
          <div className="empty-icon">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 3H5C3.89 3 3 3.89 3 5V19C3 20.11 3.89 21 5 21H19C20.11 21 21 20.11 21 19V5C21 3.89 20.11 3 19 3ZM19 19H5V8H19V19ZM7 10H9V12H7V10ZM11 10H13V12H11V10ZM15 10H17V12H15V10Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h3>No Branches Found</h3>
          <p>There are currently no branches configured for working days.</p>
          <button className="add-branch-btn">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Add Branch
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="working-days-container">
      <div className="page-header">
        <h2>Branch Working Days</h2>
        <div className="legend">
          <div className="legend-item">
            <span className="legend-dot active"></span>
            <span>Working Day</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot inactive"></span>
            <span>Non-Working Day</span>
          </div>
        </div>
      </div>

      <div className="working-days-configurations">
        {groupedData.map((workingDay, index) => (
          <div
            key={workingDay.workingDayId}
            className="working-day-configuration"
          >
            <div className="configuration-header">
              <div className="configuration-info">
                <h3 className="configuration-title">
                  {workingDay.workingTypeName}
                </h3>
                <span className="configuration-subtitle">
                  {workingDay.branches?.length || 0} branches configured
                </span>
              </div>
              <div className="configuration-actions">
                {editMode[workingDay.workingDayId] ? (
                  <>
                    <IconButton
                      icon="fluent:save-24-regular"
                      onClick={() =>
                        saveWorkingDays(workingDay.workingDayId, index)
                      }
                      className="save-button"
                      variant="save"
                    />
                    <IconButton
                      icon="ic:round-cancel"
                      onClick={() => toggleEditMode(workingDay.workingDayId)}
                      variant="cancel"
                      className="cancel-button"
                    />
                  </>
                ) : (
                  <>
                    <IconButton
                      icon="iconamoon:edit-light"
                      onClick={() => handleEditClick(workingDay)}
                      variant="edit"
                    />
                    <IconButton
                      icon="fluent:delete-28-regular"
                      onClick={() =>
                        setWarningPopup({
                          isOpen: true,
                          workingDayId: workingDay.workingDayId,
                        })
                      }
                      variant="delete"
                    />
                  </>
                )}
              </div>
            </div>

            <div className="weekly-schedule-section">
              <h4>Weekly Schedule</h4>
              <div className="weekly-schedule">
                <div className="days-row">
                  {renderWeekDots(
                    workingDay.monday,
                    "Mon",
                    editMode[workingDay.workingDayId],
                    (e) => handleInputChange(e, index, "monday")
                  )}
                  {renderWeekDots(
                    workingDay.tuesday,
                    "Tue",
                    editMode[workingDay.workingDayId],
                    (e) => handleInputChange(e, index, "tuesday")
                  )}
                  {renderWeekDots(
                    workingDay.wednesday,
                    "Wed",
                    editMode[workingDay.workingDayId],
                    (e) => handleInputChange(e, index, "wednesday")
                  )}
                  {renderWeekDots(
                    workingDay.thursday,
                    "Thu",
                    editMode[workingDay.workingDayId],
                    (e) => handleInputChange(e, index, "thursday")
                  )}
                  {renderWeekDots(
                    workingDay.friday,
                    "Fri",
                    editMode[workingDay.workingDayId],
                    (e) => handleInputChange(e, index, "friday")
                  )}
                  {renderWeekDots(
                    workingDay.saturday,
                    "Sat",
                    editMode[workingDay.workingDayId],
                    (e) => handleInputChange(e, index, "saturday")
                  )}
                  {renderWeekDots(
                    workingDay.sunday,
                    "Sun",
                    editMode[workingDay.workingDayId],
                    (e) => handleInputChange(e, index, "sunday")
                  )}
                </div>
              </div>
            </div>

            <div className="special-schedule-section">
              <h4>Special Days Schedule</h4>
              <div className="special-schedule">
                <div className="special-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Week 1</th>
                        <th>Week 2</th>
                        <th>Week 3</th>
                        <th>Week 4</th>
                        <th>Week 5</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="day-label">Saturday</td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnFirstSaturday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(
                                e,
                                index,
                                "leaveOnFirstSaturday"
                              )
                          )}
                        </td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnSecondSaturday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(
                                e,
                                index,
                                "leaveOnSecondSaturday"
                              )
                          )}
                        </td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnThirdSaturday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(
                                e,
                                index,
                                "leaveOnThirdSaturday"
                              )
                          )}
                        </td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnFourthSaturday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(
                                e,
                                index,
                                "leaveOnFourthSaturday"
                              )
                          )}
                        </td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnFifthSaturday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(
                                e,
                                index,
                                "leaveOnFifthSaturday"
                              )
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="day-label">Sunday</td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnFirstSunday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(e, index, "leaveOnFirstSunday")
                          )}
                        </td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnSecondSunday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(e, index, "leaveOnSecondSunday")
                          )}
                        </td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnThirdSunday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(e, index, "leaveOnThirdSunday")
                          )}
                        </td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnFourthSunday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(e, index, "leaveOnFourthSunday")
                          )}
                        </td>
                        <td>
                          {renderSpecialDayDot(
                            workingDay.leaveOnFifthSunday,
                            editMode[workingDay.workingDayId],
                            (e) =>
                              handleInputChange(e, index, "leaveOnFifthSunday")
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="branches-section">
              <h4>Branches & Departments</h4>
              <div className="branch-groups">
                {workingDay.branchGroups?.map((group, groupIndex) => (
                  <div key={groupIndex} className="branch-group">
                    <div className="group-header">
                      <div className="departments-list">
                        {group.departments && group.departments.length > 0 ? (
                          group.departments.map((department) => (
                            <span
                              key={department.departmentId}
                              className="department-badge"
                            >
                              {department.departmentName}
                            </span>
                          ))
                        ) : (
                          <span className="no-departments">
                            No departments assigned
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="branches-list">
                      {group.branches.map((branch) => (
                        <div key={branch.branchId} className="branch-item">
                          <div className="branch-icon">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 2L2 7V10C2 16 6 20.5 12 22C18 20.5 22 16 22 10V7L12 2Z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                          <span className="branch-name">
                            {branch.branchName}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <WarningPopup
        isOpen={warningPopup.isOpen}
        onClose={handleCancelDelete}
        onAccept={handleConfirmDelete}
        onDecline={handleCancelDelete}
        title="Delete Confirmation"
        message="Are you sure you want to delete this working day configuration? This action cannot be undone."
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default BranchWorkingDaysCard;
